import { getAuth } from '../../auth/core/AuthHelpers'

const API_URL = process.env.REACT_APP_API_URL || '/api'

const normalizeApiBase = (value: string) => value.replace(/\/+$/, '')
const isAbsoluteUrl = (value: string) => /^https?:\/\//i.test(value)
const buildApiUrl = (path: string) => {
  const trimmed = String(path || '').trim()
  if (!trimmed) return trimmed
  if (isAbsoluteUrl(trimmed)) return trimmed

  const apiBase = normalizeApiBase(API_URL)
  if (!apiBase) return trimmed

  const normalizedPath = trimmed.startsWith('/') ? trimmed : `/${trimmed}`
  const normalizedApiBase = apiBase.endsWith('/api') ? apiBase : `${apiBase}/api`
  const pathWithoutApiPrefix = normalizedPath.replace(/^\/api(?=\/|$)/i, '')
  if (isAbsoluteUrl(apiBase)) {
    return `${normalizedApiBase}${pathWithoutApiPrefix}`
  }
  return `${normalizedApiBase}${pathWithoutApiPrefix}`
}

export const ATTACHMENT_ID_MISSING_MESSAGE =
  'Attachment id is missing. Please re-open the page or contact support.'

export type ProcurementAttachmentRef =
  | string
  | number
  | {
    id?: number | string | null
    registryId?: number | string | null
    attachmentKey?: string | null
    url?: string | null
    fileName?: string | null
    viewUrl?: string | null
    downloadUrl?: string | null
  }
  | null
  | undefined

const parseAttachmentIdFromApiUrl = (value?: string | null): string | null => {
  const trimmed = String(value || '').trim()
  if (!trimmed) return null
  const match = trimmed.match(/\/procurement\/attachments\/(\d+)(?:\/|$)/i)
  return match?.[1] || null
}

const parseSecureAttachmentUrl = (
  ref: ProcurementAttachmentRef,
  mode: 'view' | 'download'
): string | null => {
  if (!ref || typeof ref === 'number') return null
  if (typeof ref === 'string') {
    const trimmed = ref.trim()
    return /\/procurement\/attachments\/\d+(?:\/|$)/i.test(trimmed) ? buildApiUrl(trimmed) : null
  }
  const candidate = mode === 'view' ? ref.viewUrl : ref.downloadUrl
  const trimmed = String(candidate || '').trim()
  return /\/procurement\/attachments\/\d+(?:\/|$)/i.test(trimmed) ? buildApiUrl(trimmed) : null
}

const resolveAttachmentId = (ref: ProcurementAttachmentRef): string | null => {
  if (ref == null) return null
  if (typeof ref === 'number') return String(ref)
  if (typeof ref === 'string') {
    const trimmed = ref.trim()
    if (!trimmed) return null
    if (trimmed.startsWith('attachment:')) {
      return trimmed.substring('attachment:'.length)
    }
    const fromUrl = parseAttachmentIdFromApiUrl(trimmed)
    if (fromUrl) return fromUrl
    if (/^\d+$/.test(trimmed)) return trimmed
    return null
  }
  if (ref.registryId != null && String(ref.registryId).trim()) return String(ref.registryId).trim()
  if (ref.id != null && String(ref.id).trim()) return String(ref.id).trim()
  if (ref.attachmentKey) {
    const trimmed = String(ref.attachmentKey).trim()
    if (trimmed.startsWith('attachment:')) {
      return trimmed.substring('attachment:'.length)
    }
    if (/^\d+$/.test(trimmed)) {
      return trimmed
    }
  }
  if (ref.url && String(ref.url).trim().startsWith('attachment:')) {
    return String(ref.url).trim().substring('attachment:'.length)
  }
  const fromViewUrl = parseAttachmentIdFromApiUrl(ref.viewUrl)
  if (fromViewUrl) return fromViewUrl
  const fromDownloadUrl = parseAttachmentIdFromApiUrl(ref.downloadUrl)
  if (fromDownloadUrl) return fromDownloadUrl
  return null
}

export const getProcurementAttachmentViewUrl = (id: number | string) => {
  const apiBase = normalizeApiBase(API_URL)
  const prefix = apiBase.endsWith('/api') ? '' : '/api'
  return `${apiBase}${prefix}/procurement/attachments/${encodeURIComponent(String(id))}/view`
}

export const getProcurementAttachmentDownloadUrl = (id: number | string) => {
  const apiBase = normalizeApiBase(API_URL)
  const prefix = apiBase.endsWith('/api') ? '' : '/api'
  return `${apiBase}${prefix}/procurement/attachments/${encodeURIComponent(String(id))}/download`
}

export const resolveProcurementDownloadUrl = (ref: ProcurementAttachmentRef) => {
  const explicit = parseSecureAttachmentUrl(ref, 'download')
  if (explicit) return explicit
  const attachmentId = resolveAttachmentId(ref)
  if (attachmentId) {
    return getProcurementAttachmentDownloadUrl(attachmentId)
  }
  return null
}

export const resolveProcurementViewUrl = (ref: ProcurementAttachmentRef) => {
  const explicit = parseSecureAttachmentUrl(ref, 'view')
  if (explicit) return explicit
  const attachmentId = resolveAttachmentId(ref)
  if (attachmentId) {
    return getProcurementAttachmentViewUrl(attachmentId)
  }
  return null
}

/** @deprecated Procurement attachments must use secured attachment API ids. */
export const resolveAttachmentUrl = (_rawUrl?: string | null) => ''

/** @deprecated Procurement attachments must use secured attachment API ids. */
export const normalizeLegacyProcurementFileUrl = (_rawUrl?: string | null) => ''

const getAuthHeader = () => {
  const token = getAuth()?.auth?.jwt
  return token ? { Authorization: `Bearer ${token}` } : undefined
}

const parseContentDispositionFileName = (value: string | null): string | null => {
  if (!value) return null
  const utf8Match = value.match(/filename\*\s*=\s*UTF-8''([^;]+)/i)
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1].trim())
    } catch {
      return utf8Match[1].trim()
    }
  }
  const plainMatch = value.match(/filename\s*=\s*"?([^";]+)"?/i)
  return plainMatch?.[1]?.trim() || null
}

const ATTACHMENT_ERROR_MESSAGE =
  'Unable to open attachment. Please try again or contact support.'

const fetchAttachmentResponse = async (
  ref: ProcurementAttachmentRef,
  mode: 'view' | 'download'
): Promise<{ blob: Blob; fileName: string | null }> => {
  const attachmentId = resolveAttachmentId(ref)
  const url =
    mode === 'view' ? resolveProcurementViewUrl(ref) : resolveProcurementDownloadUrl(ref)

  if (!url) {
    console.error('Procurement attachment id missing', { ref, mode })
    throw new Error(ATTACHMENT_ID_MISSING_MESSAGE)
  }

  const authHeaders = getAuthHeader()
  const response = await fetch(url, {
    headers: authHeaders,
  })
  if (response.status === 403) {
    throw new Error('You are not authorized to access this attachment.')
  }
  if (response.status === 404) {
    throw new Error('Attachment file not found.')
  }
  if (!response.ok) {
    console.error('Procurement attachment fetch failed', {
      attachmentId,
      mode,
      status: response.status,
      url,
    })
    throw new Error(ATTACHMENT_ERROR_MESSAGE)
  }
  const contentType = response.headers.get('content-type') || undefined
  if (contentType && contentType.toLowerCase().includes('text/html')) {
    throw new Error('Attachment endpoint returned HTML instead of a file.')
  }
  const serverFileName = parseContentDispositionFileName(response.headers.get('content-disposition'))
  const rawBlob = await response.blob()
  const blob =
    contentType && rawBlob.type !== contentType ? new Blob([rawBlob], { type: contentType }) : rawBlob
  return { blob, fileName: serverFileName }
}

export const downloadAttachmentBlob = async (
  ref: ProcurementAttachmentRef,
  fileName?: string | null
) => {
  try {
    const { blob, fileName: serverFileName } = await fetchAttachmentResponse(ref, 'download')
    const blobUrl = window.URL.createObjectURL(blob)
    const link = window.document.createElement('a')
    link.href = blobUrl
    link.download = String(serverFileName || fileName || 'attachment')
    window.document.body.appendChild(link)
    link.click()
    window.document.body.removeChild(link)
    window.URL.revokeObjectURL(blobUrl)
  } catch (error) {
    console.error('Procurement attachment download failed', { ref, fileName, error })
    throw error
  }
}

export const previewAttachmentBlob = async (ref: ProcurementAttachmentRef) => {
  try {
    const { blob } = await fetchAttachmentResponse(ref, 'view')
    const blobUrl = window.URL.createObjectURL(blob)
    window.open(blobUrl, '_blank', 'noopener,noreferrer')
    window.setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60_000)
  } catch (error) {
    console.error('Procurement attachment preview failed', { ref, error })
    throw error
  }
}

export const viewProcurementAttachment = previewAttachmentBlob
export const downloadProcurementAttachment = downloadAttachmentBlob

export const createAttachmentPreviewUrl = async (ref: ProcurementAttachmentRef) => {
  const { blob } = await fetchAttachmentResponse(ref, 'view')
  return window.URL.createObjectURL(blob)
}

export { resolveAttachmentId }
export { buildApiUrl }
